import { useCallback, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { Toast } from 'primereact/toast'

import {
  ApiError,
  ExcelPlanResponse,
  Item,
  OptimizePlanRequest,
  OptimizePlanResponse,
  ProductionPlanService,
  FilesService,
  GenerateXMLRequest,
  GenerateXMLResponse,
} from '@/api'
import { excelPlanResponseAtom, optimizePlanRequestAtom, optimizePlanResponseAtom, originalPlanResponseAtom } from '@/atoms/optimization'
import { normalizeOptimizePlanResponse } from '@/utils/normalizeOptimizeResponse'
import { deriveLineMaster, filterExistingLines } from '@/utils/lineMaster'
import { ItemStats, ItemTreeTableProps } from '../components/ItemTreeTable'
import { extractToNumbersFromNumberWithBold, NumberWithBold } from '../types'
import { useMutationTaskWithProgress } from '@/api/hooks/useTaskWithProgress'

export type ExcelReloadStep = 'downloading' | 'reading' | 'optimizing'
export type ExcelReloadStage = ExcelReloadStep | 'idle' | 'success' | 'error'
export type ExcelReloadStatus = 'pending' | 'active' | 'completed' | 'error'
type ItemStatusFlag = 'empty' | 'little' | 'over'

export const excelReloadSteps: { id: ExcelReloadStep; label: string }[] = [
  { id: 'downloading', label: 'OneDriveから最新ファイルを取得' },
  { id: 'reading', label: 'Excelデータを再読み込み' },
  { id: 'optimizing', label: 'シミュレーションを再実行' },
]

const rebuildOptimizePlanRequestFromExcel = (
  prev: OptimizePlanRequest | null,
  planResponse: ExcelPlanResponse,
): OptimizePlanRequest | null => {
  if (!prev) {
    return null
  }

  const planDateIndex = new Map((planResponse.dates ?? []).map((date, idx) => [date, idx]))
  // 再読込したExcelで品目が消えた・製造分類が変わった場合、旧設定の稼働ラインが実在しなくなる
  const lineMaster = deriveLineMaster(planResponse.itemMaster)
  const excelProductions = planResponse.itemMaster.reduce<Record<string, Array<{ value: number; is_bold?: boolean }>>>((acc, item) => {
    acc[item.品目ID] = item.生産 ?? []
    return acc
  }, {})
  const excelShipments = planResponse.itemMaster.reduce<Record<string, Array<{ value: number }>>>((acc, item) => {
    acc[item.品目ID] = item.出荷 ?? []
    return acc
  }, {})
  const excelInventories = planResponse.itemMaster.reduce<Record<string, Array<{ value: number }>>>((acc, item) => {
    acc[item.品目ID] = item.在庫 ?? []
    return acc
  }, {})

  const existingItemIds = new Set<string>()
  prev.days.forEach((day) => {
    Object.keys(day.items ?? {}).forEach((itemId) => existingItemIds.add(itemId))
  })
  planResponse.itemMaster.forEach((item) => existingItemIds.add(item.品目ID))
  const targetItemIds = Array.from(existingItemIds)

  const updatedDays = prev.days.map((day) => {
    const sourceIdx = planDateIndex.get(day.date)
    const nextItems: typeof day.items = {}

    targetItemIds.forEach((itemId) => {
      const prevState = day.items?.[itemId]
      let production = prevState?.production
      let shipment = prevState?.shipment
      let inventory = prevState?.inventory

      if (sourceIdx !== undefined) {
        const prod = excelProductions[itemId]?.[sourceIdx]
        if (prod) {
          production = { value: prod.value, is_bold: !!prod.is_bold }
        }
        const ship = excelShipments[itemId]?.[sourceIdx]
        if (ship) {
          shipment = { value: ship.value }
        }
        const inv = excelInventories[itemId]?.[sourceIdx]
        if (inv) {
          inventory = { value: inv.value }
        }
      }

      if (!production) {
        production = { value: 0, is_bold: false }
      }

      nextItems[itemId] = {
        production,
        shipment,
        inventory,
      }
    })

    return {
      ...day,
      capacity: { ...day.capacity, lines: filterExistingLines(day.capacity.lines, lineMaster) },
      items: nextItems,
    }
  })

  return { ...prev, days: updatedDays }
}

export type PlanActionBarConfig = {
  onSimulate: () => void
  onXmlExport: () => void
  onExcelReload: () => void
  onExcelOverwrite: () => void
  onCheckoutOnly: () => void
  onAdjustSettings: () => void
  isSimulatePending: boolean
  isXmlPending: boolean
  isExcelPending: boolean
  isCheckoutPending: boolean
  isExcelReloading: boolean
  disableXmlExport: boolean
  disableExcelReload: boolean
  disableExcelOverwrite: boolean
  disableCheckoutOnly: boolean
}

export const usePlanPage = () => {
  const toastRef = useRef<Toast>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const excelPlanResponse = useAtomValue(excelPlanResponseAtom)
  const [originalPlanResponse, setOriginalPlanResponse] = useAtom(originalPlanResponseAtom)
  const [optimizePlanResponse, setOptimizePlanResponse] = useAtom(optimizePlanResponseAtom)
  const optimizeRequest = useAtomValue(optimizePlanRequestAtom)
  const setExcelPlanResponse = useSetAtom(excelPlanResponseAtom)
  const setOptimizePlanRequest = useSetAtom(optimizePlanRequestAtom)

  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([])
  const [isCategory, setIsCategory] = useState(false)
  const [optimizerVisible, setOptimizerVisible] = useState(false)
  const [xmlModalVisible, setXmlModalVisible] = useState(false)
  const [xmlResponse, setXmlResponse] = useState<GenerateXMLResponse | null>(null)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [excelReloadModalVisible, setExcelReloadModalVisible] = useState(false)
  const [excelReloadStage, setExcelReloadStage] = useState<ExcelReloadStage>('idle')
  const [excelReloadFailedStep, setExcelReloadFailedStep] = useState<ExcelReloadStep | null>(null)
  const [excelReloadError, setExcelReloadError] = useState<string | null>(null)
  const [userProductionChanges, setUserProductionChanges] = useState<Record<string, Record<number, NumberWithBold>>>({})
  const [excelWriteDialogVisible, setExcelWriteDialogVisible] = useState(false)
  const [excelWriteError, setExcelWriteError] = useState<string | null>(null)
  const [excelWriteLocked, setExcelWriteLocked] = useState(false)
  const [excelWriteSuccessful, setExcelWriteSuccessful] = useState(false)

  const isExcelReloading = excelReloadStage === 'downloading' || excelReloadStage === 'reading' || excelReloadStage === 'optimizing'

  const handleSelectionChange = useCallback((items: Item[], category: boolean) => {
    setSelectedItemIds(items.map((item) => item.品目ID))
    setSelectedDate(null)
    setIsCategory(category)
  }, [])


  const resetExcelReloadState = useCallback(() => {
    setExcelReloadStage('idle')
    setExcelReloadFailedStep(null)
    setExcelReloadError(null)
  }, [])

  const closeExcelReloadModal = useCallback(() => {
    if (isExcelReloading) {
      return
    }
    setExcelReloadModalVisible(false)
    resetExcelReloadState()
  }, [isExcelReloading, resetExcelReloadState])

  const extractReloadErrorMessage = useCallback((error: unknown): string => {
    if (error && typeof error === 'object') {
      const err = error as { message?: string; body?: { detail?: string } }
      if (err.body?.detail) {
        return String(err.body.detail)
      }
      if (err.message) {
        return err.message
      }
    }
    return '不明なエラーが発生しました'
  }, [])

  const getReloadStepStatus = useCallback(
    (step: ExcelReloadStep): ExcelReloadStatus => {
      if (excelReloadStage === 'success') {
        return 'completed'
      }
      if (excelReloadStage === 'error') {
        if (excelReloadFailedStep === step) {
          return 'error'
        }
        if (!excelReloadFailedStep) {
          return 'pending'
        }
        const failedIndex = excelReloadSteps.findIndex((s) => s.id === excelReloadFailedStep)
        const stepIndex = excelReloadSteps.findIndex((s) => s.id === step)
        return stepIndex < failedIndex ? 'completed' : 'pending'
      }
      const currentIndex = excelReloadSteps.findIndex((s) => s.id === excelReloadStage)
      const stepIndex = excelReloadSteps.findIndex((s) => s.id === step)
      if (currentIndex === -1) {
        return 'pending'
      }
      if (currentIndex > stepIndex) {
        return 'completed'
      }
      if (currentIndex === stepIndex) {
        return 'active'
      }
      return 'pending'
    },
    [excelReloadStage, excelReloadFailedStep],
  )

  const excelReloadProgress = useMemo(
    () => excelReloadSteps.map((step) => ({ ...step, status: getReloadStepStatus(step.id) })),
    [getReloadStepStatus],
  )

  const items = useMemo(() => excelPlanResponse?.itemMaster ?? [], [excelPlanResponse])

  const selectedItems = useMemo(() => {
    if (!selectedItemIds.length) {
      return []
    }
    const idSet = new Set(selectedItemIds)
    return items.filter((item) => idSet.has(item.品目ID))
  }, [items, selectedItemIds])
  const currentResponse = optimizePlanResponse
  const availableDates = useMemo(() => currentResponse?.dates ?? [], [currentResponse])

  const [itemsStatus, itemStats] = useMemo(() => {
    const status: ItemTreeTableProps['itemStatus'] = {}
    const stats: ItemTreeTableProps['itemStats'] = {}

    items.forEach((item) => {
      const inventory = currentResponse?.inventories?.[item.品目ID] ?? []
      const shipments = currentResponse?.shipments?.[item.品目ID] ?? []
      const productionsList = currentResponse?.productions?.[item.品目ID] ?? []

      const set = new Set<ItemStatusFlag>()
      if (inventory.some((i) => i.value < 0)) {
        set.add('empty')
      }
      if (inventory.some((i) => i.value < item.在庫下限.value)) {
        set.add('little')
      }
      if (inventory.some((i) => i.value > item.在庫上限.value)) {
        set.add('over')
      }
      status[item.品目ID] = set

      stats[item.品目ID] = {
        在庫切れ日数: inventory.filter((i) => i.value < 0).length,
        在庫切れ個数: inventory.filter((i) => i.value < 0).reduce((acc, i) => acc + i.value, 0),
        総生産: productionsList.reduce((acc, i) => acc + i.value, 0),
        総出荷: shipments.reduce((acc, i) => acc + i.value, 0),
      } satisfies ItemStats
    })

    return [status, stats]
  }, [items, currentResponse])

  const inventories = useMemo(() => {
    const result: Record<Item['品目ID'], number[]> = {}
    items.forEach((item) => {
      result[item.品目ID] = currentResponse?.inventories?.[item.品目ID]?.map((i) => i.value) ?? []
      result[`${item.品目ID}_old`] = originalPlanResponse?.inventories?.[item.品目ID]?.map((i) => i.value) ?? []
    })
    return result
  }, [items, currentResponse, originalPlanResponse])

  const shipments = useMemo(() => {
    const result: Record<Item['品目ID'], number[]> = {}
    items.forEach((item) => {
      result[item.品目ID] = currentResponse?.shipments?.[item.品目ID]?.map((i) => i.value) ?? []
      result[`${item.品目ID}_old`] = originalPlanResponse?.shipments?.[item.品目ID]?.map((i) => i.value) ?? []
    })
    return result
  }, [items, currentResponse, originalPlanResponse])

  const excelProductions = useMemo(() => {
    const result: Record<Item['品目ID'], NumberWithBold[]> = {}
    items.forEach((item) => {
      result[item.品目ID] = item.生産?.map((p) => ({ value: p.value, is_bold: p.is_bold })) ?? []
    })
    return result
  }, [items])

  const optimizerProductions = useMemo(() => {
    const result: Record<Item['品目ID'], NumberWithBold[]> = {}
    items.forEach((item) => {
      result[item.品目ID] = currentResponse?.productions?.[item.品目ID]?.map((i) => ({ value: i.value, is_bold: i.is_bold })) ?? []
    })
    return result
  }, [items, currentResponse])

  const productions = useMemo(() => {
    const result: Record<Item['品目ID'], NumberWithBold[]> = {}
    items.forEach((item) => {
      const itemId = item.品目ID
      result[itemId] = currentResponse?.productions?.[itemId]?.map((i) => ({ value: i.value, is_bold: i.is_bold })) ?? []
      result[`${itemId}_old`] = originalPlanResponse?.productions?.[itemId]?.map((i) => ({ value: i.value, is_bold: i.is_bold })) ?? []
    })
    return result
  }, [items, currentResponse, originalPlanResponse])

  const numericOptimizerProductions = useMemo(
    () => extractToNumbersFromNumberWithBold(optimizerProductions),
    [optimizerProductions],
  )
  const numericProductions = useMemo(() => extractToNumbersFromNumberWithBold(productions), [productions])

  const selectedFileQuery = useQuery({
    queryKey: ['selectedFile'],
    queryFn: () => FilesService.selectedFiles(),
    enabled: true,
    refetchOnWindowFocus: false,
  })

  const optimizeQuery = useMutation({
    mutationFn: (req: OptimizePlanRequest) => ProductionPlanService.optimize(req),
    onSuccess: (data) => {
      setOriginalPlanResponse(normalizeOptimizePlanResponse(data))
    },
  })

  const generateXMLMutation = useMutation({
    mutationFn: (req: GenerateXMLRequest) => ProductionPlanService.generateXml(req),
    onSuccess: (data: GenerateXMLResponse) => {
      setXmlResponse(data)
      setXmlModalVisible(true)
      toastRef.current?.show({
        severity: 'success',
        summary: '成功',
        detail: 'XMLファイルが生成され、Exchange URLが利用可能になりました',
        life: 3000,
      })
    },
    onError: (error) => {
      console.error('XML生成エラー:', error)
      toastRef.current?.show({
        severity: 'error',
        summary: 'エラー',
        detail: 'XMLファイルの生成に失敗しました',
        life: 5000,
      })
    },
  })

  const generateExcelMutation = useMutationTaskWithProgress((req: GenerateXMLRequest) => ProductionPlanService.generateExcel(req))
  const checkoutOnlyMutation = useMutation({
    mutationFn: () => ProductionPlanService.checkoutOnly(),
  })
  const forceReleaseCheckoutMutation = useMutation({
    mutationFn: () => ProductionPlanService.forceReleaseCheckout(),
  })

  const extractExcelWriteErrorMessage = useCallback((error: unknown): string => {
    if (error && typeof error === 'object') {
      const err = error as { message?: string; body?: { detail?: string } }
      if (err.body?.detail) {
        return String(err.body.detail)
      }
      if (err.message) {
        return err.message
      }
    }
    return 'Excelファイルの更新に失敗しました'
  }, [])

  const isFileLockedError = useCallback((error: unknown): boolean => {
    if (error instanceof ApiError) {
      return error.status === 423
    }
    if (error && typeof error === 'object' && 'status' in error) {
      const status = (error as { status?: number }).status
      return status === 423
    }
    return false
  }, [])

  const buildGenerateRequest = useCallback(
    (includeBold = false): GenerateXMLRequest | null => {
      if (!currentResponse?.productions || !currentResponse?.dates) {
        toastRef.current?.show({
          severity: 'warn',
          summary: '警告',
          detail: '生産計画データがありません。最適化を実行してください',
          life: 3000,
        })
        return null
      }

      const xmlProductions: Record<string, Array<{ value: number; is_bold?: boolean }>> = {}
      Object.entries(productions).forEach(([itemId, productionList]) => {
        if (itemId.endsWith('_old')) return
        xmlProductions[itemId] = productionList.map((p) =>
          includeBold ? { value: p.value, is_bold: p.is_bold } : { value: p.value },
        )
      })

      return {
        dates: currentResponse.dates,
        productions: xmlProductions,
      }
    },
    [currentResponse, productions],
  )

  const handleXMLExport = useCallback(() => {
    const xmlRequest = buildGenerateRequest(false)
    if (!xmlRequest) return
    generateXMLMutation.mutate(xmlRequest)
  }, [buildGenerateRequest, generateXMLMutation])

  const handleExcelOverwrite = useCallback(async () => {
    const excelRequest = buildGenerateRequest(true)
    if (!excelRequest) return

    setExcelWriteDialogVisible(true)
    setExcelWriteError(null)
    setExcelWriteLocked(false)
    setExcelWriteSuccessful(false)

    try {
      await generateExcelMutation.mutateAsync([excelRequest])
      setExcelWriteSuccessful(true)
      toastRef.current?.show({
        severity: 'success',
        summary: 'Excel更新',
        detail: 'Excelファイルの書き込みが完了しました。',
        life: 3000,
      })
    } catch (error) {
      const message = extractExcelWriteErrorMessage(error)
      console.error('Excel出力エラー:', error)
      setExcelWriteError(message)
      setExcelWriteLocked(isFileLockedError(error))
      toastRef.current?.show({
        severity: 'error',
        summary: 'エラー',
        detail: message,
        life: 5000,
      })
    }
  }, [buildGenerateRequest, extractExcelWriteErrorMessage, generateExcelMutation, isFileLockedError])

  const handleForceReleaseCheckout = useCallback(async () => {
    setExcelWriteError(null)
    try {
      await forceReleaseCheckoutMutation.mutateAsync()
      setExcelWriteLocked(false)
      toastRef.current?.show({
        severity: 'success',
        summary: 'チェックアウト解除',
        detail: 'OneDriveのチェックアウト状態を解除しました。再度書き込みを実行してください。',
        life: 4000,
      })
    } catch (error) {
      const message = extractExcelWriteErrorMessage(error)
      setExcelWriteError(message)
      toastRef.current?.show({
        severity: 'error',
        summary: '解除失敗',
        detail: message,
        life: 5000,
      })
    }
  }, [extractExcelWriteErrorMessage, forceReleaseCheckoutMutation])

  const handleCheckoutOnly = useCallback(async () => {
    try {
      await checkoutOnlyMutation.mutateAsync()
      toastRef.current?.show({
        severity: 'success',
        summary: 'チェックアウト実行',
        detail: 'OneDriveファイルをチェックアウトしました。',
        life: 4000,
      })
    } catch (error) {
      const message = extractExcelWriteErrorMessage(error)
      toastRef.current?.show({
        severity: 'error',
        summary: 'チェックアウト失敗',
        detail: message,
        life: 5000,
      })
    }
  }, [checkoutOnlyMutation, extractExcelWriteErrorMessage])

  const handleExcelImport = useCallback(async () => {
    if (isExcelReloading) {
      return
    }

    let fileMeta = selectedFileQuery.data?.files?.[0]
    if (!fileMeta) {
      const refetched = await selectedFileQuery.refetch()
      fileMeta = refetched.data?.files?.[0]
    }

    if (!fileMeta) {
      toastRef.current?.show({
        severity: 'warn',
        summary: '警告',
        detail: '再読み込み対象のファイルが見つかりません',
        life: 4000,
      })
      return
    }

    if (!fileMeta.onedrive_file_id) {
      toastRef.current?.show({
        severity: 'warn',
        summary: '警告',
        detail: 'OneDriveとリンクされたファイルのみ再読み込みできます',
        life: 4000,
      })
      return
    }

    if (!optimizeRequest) {
      toastRef.current?.show({
        severity: 'warn',
        summary: '警告',
        detail: '最適化設定が未設定のため再読み込みできません。生産計画作成フローを完了してください。',
        life: 5000,
      })
      return
    }

    resetExcelReloadState()
    setExcelReloadModalVisible(true)

    let currentStep: ExcelReloadStep = 'downloading'

    try {
      setExcelReloadStage('downloading')
      await FilesService.downloadOnedriveFile({ file_id: fileMeta.onedrive_file_id })
      currentStep = 'reading'
      setExcelReloadStage(currentStep)
      const planResponse = await ProductionPlanService.createProductionPlan(1)
      setExcelPlanResponse(planResponse)
      const updatedOptimizeRequest = rebuildOptimizePlanRequestFromExcel(optimizeRequest, planResponse)
      if (!updatedOptimizeRequest) {
        throw new Error('最適化設定の再構築に失敗しました')
      }
      setOptimizePlanRequest(updatedOptimizeRequest)

      currentStep = 'optimizing'
      setExcelReloadStage(currentStep)
      const simulationRequest: OptimizePlanRequest = {
        ...updatedOptimizeRequest,
        optimize: OptimizePlanRequest.optimize.SIMULATION_ONLY,
      }
      const optimizeResult = await ProductionPlanService.optimize(simulationRequest)
      const normalizedResult = normalizeOptimizePlanResponse(optimizeResult)
      setOriginalPlanResponse(normalizedResult)
      setOptimizePlanResponse(normalizedResult)

      setExcelReloadStage('success')
      setExcelReloadFailedStep(null)
      setExcelReloadError(null)
      queryClient.invalidateQueries({ queryKey: ['selectedFile'] })
      toastRef.current?.show({
        severity: 'success',
        summary: '再読み込み完了',
        detail: 'OneDriveの最新ファイルでシミュレーションを再実行しました',
        life: 4000,
      })
    } catch (error) {
      const message = extractReloadErrorMessage(error)
      setExcelReloadFailedStep(currentStep)
      setExcelReloadStage('error')
      setExcelReloadError(message)
      toastRef.current?.show({
        severity: 'error',
        summary: '再読み込み失敗',
        detail: message,
        life: 5000,
      })
    }
  }, [
    extractReloadErrorMessage,
    isExcelReloading,
    optimizeRequest,
    queryClient,
    resetExcelReloadState,
    selectedFileQuery,
    setExcelPlanResponse,
    setOptimizePlanResponse,
    setOptimizePlanRequest,
    setOriginalPlanResponse,
  ])

  const handleOptimizeDialogClose = useCallback(() => {
    if (optimizeQuery.isPending) {
      return
    }
    setOptimizerVisible(false)
  }, [optimizeQuery.isPending])

  const handleOptimize = useCallback(
    (response: OptimizePlanResponse) => {
      setOptimizePlanResponse(normalizeOptimizePlanResponse(response))
      setOptimizerVisible(false)
    },
    [setOptimizePlanResponse],
  )

  const handleProductionChange = useCallback((itemId: string, dateIndex: number, value: number, isBold: boolean) => {
    setUserProductionChanges((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [dateIndex]: { value, is_bold: isBold },
      },
    }))
  }, [])

  const manualProductionPayload = useMemo(() => {
    const result: Record<string, Array<{ value: number; is_bold: boolean } | null>> = {}
    const dates = currentResponse?.dates ?? optimizeRequest?.days.map((day) => day.date) ?? []
    items.forEach((item) => {
      const itemId = item.品目ID
      const overrides: Array<{ value: number; is_bold: boolean } | null> = []

      dates.forEach((_, dateIndex) => {
        const userChange = userProductionChanges[itemId]?.[dateIndex]
        if (userChange?.is_bold) {
          overrides.push({ value: userChange.value, is_bold: true })
          return
        }

        const excelBold = item.生産?.[dateIndex]
        if (excelBold?.is_bold) {
          overrides.push({ value: excelBold.value, is_bold: true })
          return
        }

        const optimizerValue = currentResponse?.productions?.[itemId]?.[dateIndex]
        if (optimizerValue?.is_bold) {
          overrides.push({ value: optimizerValue.value, is_bold: true })
          return
        }

        overrides.push(null)
      })

      result[itemId] = overrides
    })

    return result
  }, [items, currentResponse, optimizeRequest, userProductionChanges])

  const previewOptimizeRequest = useMemo(() => {
    if (!optimizeRequest) return null
    const updatedDays = optimizeRequest.days.map((day, dayIndex) => {
      const nextItems: typeof day.items = {}
      Object.entries(day.items ?? {}).forEach(([itemId, state]) => {
        const overrides = manualProductionPayload[itemId]
        const overrideValue = overrides?.[dayIndex]
        if (overrideValue) {
          nextItems[itemId] = {
            ...state,
            production: { value: overrideValue.value, is_bold: true },
          }
        } else if (state.production && state.production.is_bold) {
          nextItems[itemId] = {
            ...state,
            production: { ...state.production, is_bold: false },
          }
        } else {
          nextItems[itemId] = state
        }
      })
      return { ...day, items: nextItems }
    })
    return { ...optimizeRequest, days: updatedDays }
  }, [manualProductionPayload, optimizeRequest])

  const executeOptimization = useCallback(() => {
    if (!previewOptimizeRequest) {
      toastRef.current?.show({
        severity: 'warn',
        summary: '警告',
        detail: '最適化リクエストが不正です',
        life: 3000,
      })
      return
    }
    optimizeQuery.mutate(previewOptimizeRequest)
    setOptimizerVisible(true)
  }, [optimizeQuery, previewOptimizeRequest])

  const hasSelectedFile = Boolean(selectedFileQuery.data?.files?.length)
  const isExcelWriting = generateExcelMutation.isPending || generateExcelMutation.isObserving
  const isForceReleasingCheckout = forceReleaseCheckoutMutation.isPending
  const isCheckoutPending = checkoutOnlyMutation.isPending

  const closeExcelWriteDialog = useCallback(() => {
    if (isExcelWriting || isForceReleasingCheckout) {
      return
    }
    setExcelWriteDialogVisible(false)
    setExcelWriteError(null)
    setExcelWriteLocked(false)
    setExcelWriteSuccessful(false)
  }, [isExcelWriting, isForceReleasingCheckout])

  const actionBarConfig: PlanActionBarConfig = {
    onSimulate: executeOptimization,
    onXmlExport: handleXMLExport,
    onExcelReload: handleExcelImport,
    onExcelOverwrite: handleExcelOverwrite,
    onCheckoutOnly: handleCheckoutOnly,
    onAdjustSettings: () => navigate({ to: '/create-plan' }),
    isSimulatePending: optimizeQuery.isPending,
    isXmlPending: generateXMLMutation.isPending,
    isExcelPending: isExcelWriting,
    isCheckoutPending,
    isExcelReloading,
    disableXmlExport: !currentResponse?.productions,
    disableExcelReload:
      !hasSelectedFile || isExcelWriting || generateXMLMutation.isPending || isExcelReloading,
    disableExcelOverwrite:
      !hasSelectedFile || isExcelWriting || generateXMLMutation.isPending,
    disableCheckoutOnly:
      !hasSelectedFile || isCheckoutPending || isExcelWriting || generateXMLMutation.isPending,
  }

  return {
    toastRef,
    items,
    availableDates,
    excelPlanResponse,
    currentResponse,
    itemsStatus,
    itemStats,
    inventories,
    shipments,
    productions,
    excelProductions,
    optimizerProductions,
    numericOptimizerProductions,
    numericProductions,
    selectedItems,
    isCategory,
    selectedDate,
    optimizationLogs: currentResponse?.logs ?? [],
    handleSelectionChange,
    setSelectedDate,
    handleProductionChange,
    userProductionChanges,
    optimizerVisible,
    previewOptimizeRequest,
    optimizeQueryData: optimizeQuery.data,
    handleOptimize,
    handleOptimizeDialogClose,
    xmlModalVisible,
    setXmlModalVisible,
    xmlResponse,
    setUserProductionChanges,
    actionBarConfig,
    excelReloadDialog: {
      visible: excelReloadModalVisible,
      close: closeExcelReloadModal,
      progress: excelReloadProgress,
      stage: excelReloadStage,
      error: excelReloadError,
      isExcelReloading,
    },
    excelWriteDialog: {
      visible: excelWriteDialogVisible,
      close: closeExcelWriteDialog,
      isWriting: isExcelWriting || isForceReleasingCheckout,
      progress: generateExcelMutation.progress,
      lastLog: generateExcelMutation.lastLog,
      logs: generateExcelMutation.logs,
      error: excelWriteError,
      isLocked: excelWriteLocked,
      isSuccess: excelWriteSuccessful,
      isForceReleasing: isForceReleasingCheckout,
      forceReleaseCheckout: handleForceReleaseCheckout,
    },
  }
}
